import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from "react";

import { Card, Grid, useTheme, Theme, Box, Button } from "@mui/material";
import classNames from "classnames";

import {
  FlightRemovalType,
  hasPermission,
  ICanceledFlight,
  ICustomTab,
  IFlightPairingDetails,
  IFullFlight as IFullFlightData,
  IGroundEvent as IGroundEventData,
  PolicyAction,
  PolicyResource,
  unassignedAircraftId,
} from "@vmosolutions/oc-interfaces";
import { useSelector, useDispatch } from "react-redux";
import { OcTabs } from "src/components/OcTabs/OcTabs";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { openModal, closeModal } from "src/redux/actions/App.Actions";
import { CopyToClipboard, FormTabWrapper, getEventEditorTabs, IFormHandle } from "./helpers";

// Get Flight Content Components
import { FlightChip } from "src/components/Flights/FlightChip";
import { CommentsTab } from "./FlightTabs/CommentsTab";
import { ContentTab } from "./FlightTabs/ContentTab";
import { CrewTab } from "./FlightTabs/CrewTab";
import { CustomTab } from "./FlightTabs/CustomTab";
import { DelaysTab } from "./FlightTabs/DelaysTab";
import { FlightTab } from "./FlightTabs/FlightTab";
import { LogTab } from "./FlightTabs/LogTab";
import { MessagesTab } from "./FlightTabs/MessagesTab";

// Get Ground Event Content Components
import { EventChip } from "src/components/Flights/EventChip";
import { GECommentsTab } from "./GroundEventTabs/GECommentsTab";
import { GroundEventTab } from "./GroundEventTabs/GroundEventTab";

// Warning Components
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { DialogModal } from "src/components/DialogModal/DialogModal";
import { SendAPISMessageModal } from "src/components/DialogModal/FlightContextMenuModals/SendAPISMessageModal";
import { WarningModal } from "src/components/ModalComponents/WarningModal";
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
import { addEventToTimeline } from "src/redux/actions/flights/FlightData.Actions";
import { AlertModel, IAlert } from "src/redux/state/Alert";
import { store } from "src/redux/store/Store";
import { CancelFlightTab } from "./CanceledFlightTabs/CancelFlightTab";
import { EditorChangeWarning, ILastModifiedInfo } from "./EditorChangeWarning";

interface IEventEditorProps {
  id: string;
  path: string[];
  splitPercentage?: number;
  windowProps: {
    eventId: string;
    isFlight: boolean;
    aircraftId: string;
    removalType?: FlightRemovalType;
  };
  setCloseWarning: (warning: string) => void;
  classes: any;
}

export interface ITabElement {
  wrapper: JSX.Element;
  id: string;
}

export interface IFullFlight extends IFullFlightData {
  reInitializeForm?: boolean; // Add new boolean to flight so that if true, form gets re-initialized
}
//TODO make this more explicit so that each tab has very clearly defined props in its typescript.
export type IEditPartialFlight = Partial<IFullFlight>;

export interface IGroundEvent extends IGroundEventData {
  reInitializeForm?: boolean; // Add new boolean to flight so that if true, form gets re-initialized
}

//TODO Remove this function once the incorrect epoch format has been fixed for ground event times.
// These are currently coming back as timestamp "strings" as opposed to numbers.
export const returnEpochValuesAsNumbers = (ge: IGroundEvent): IGroundEvent => {
  const scheduledStart =
    ge?.scheduledStart && typeof ge?.scheduledStart === "string"
      ? new Date(ge?.scheduledStart)?.valueOf()
      : ge?.scheduledStart;
  const scheduledEnd =
    ge?.scheduledEnd && typeof ge?.scheduledEnd === "string" ? new Date(ge?.scheduledEnd)?.valueOf() : ge?.scheduledEnd;
  const actualStart =
    ge?.actualStart && typeof ge?.actualStart === "string" ? new Date(ge?.actualStart)?.valueOf() : ge?.actualStart;
  const actualEnd =
    ge?.actualEnd && typeof ge?.actualEnd === "string" ? new Date(ge?.actualEnd)?.valueOf() : ge?.actualEnd;
  // console.log({ ...ge, scheduledStart, scheduledEnd, actualStart, actualEnd });
  return { ...ge, scheduledStart, scheduledEnd, actualStart, actualEnd };
};

const eventEditorStyles = (theme: Theme): object => ({
  height: "fit-content",
  flexWrap: "nowrap",
  backgroundColor: theme.palette.background.paper,
  "& .MuiCardContent-root.form_card": {
    padding: 0,
    width: "100%",
    maxWidth: 750,
    "& .form_header": {
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      margin: "0px 10px 0px 10px",
      "& .form-title": { color: theme.palette.primary.contrastText, margin: 0, padding: 0 },
      "& .form-subtitle": {
        color: theme.custom.forms.labels,
        margin: "0px 10px",
        fontSize: 14,
        alignSelf: "center",
      },
      "& .form-warning": {
        color: theme.custom.modal.warning,
        margin: "0px 10px",
        fontSize: 14,
        height: "1.2em",
        alignSelf: "center",
      },
    },
    "& .form_body": {
      "& .formik_field": { width: 263 },
      "& .flightTypegroup": { width: 263 },
      "& .checkfield.diverted": { width: 110 },
      "& .datetimefield": { width: 263 },
      "& .datetimefield.blocktimes ": { alignItems: "flex-start" },
      "& .textfield.gate": {
        width: 150,
        "& .input_control": {
          "& label": { width: "auto" },
          "& div.react-select-container": {
            width: 90,
            flexGrow: 0,
          },
        },
      },
      "& .textfield.multiline_long": {
        width: "calc(100% - 10px)",
      },
    },
  },
  "& .form_footer, & .inline_button": {
    marginTop: "10px",
    display: "flex",
    justifyContent: "flex-start",
    "& button": { margin: "5px 5px" },
    "& .btn": {
      flexWrap: "wrap",
      display: "flex",
      fontSize: 12,
      lineHeight: "14px",
      padding: "8px 20px",
      margin: "0px 4px",
      "&.submit": {
        color: theme.custom.forms.inputBack,
      },
    },
  },
});

const activeEventMenuStyles = (theme: Theme): object => ({
  display: "flex",
  flexDirection: "row",
  zIndex: 1,
  "& .eventchip_container": { margin: "0px 6px 4px 6px" },
  "&.vertical": {
    flexDirection: "column",
    maxWidth: "135px", // set max width in vertical mode.
    margin: "9px 0px", // Set margin to match top/bottom of menu with Card.
  },
  "& .form-tab.MuiTab-wrapped": {
    display: "flex",
    flexDirection: "row-reverse", // so that edit icon appears to the right of the button text.
    "&.dirty": {
      color: theme.custom.forms.dirtyColor,
    },
    "& .MuiSvgIcon-root": {
      width: 14,
      height: 14,
      border: "1px solid",
      borderRadius: "3px",
      margin: "0px 5px",
    },
  },
});

const activeEventCardStyles = (theme: Theme): object => ({
  flexGrow: 1,
  boxShadow: theme.shadows[5],
  margin: "0px 8px 8px 8px",
  minHeight: "calc(100% - 20px)", // Extends the card to the max height of the parent even if it doesn't have content.
  backgroundColor: theme.custom.timeline.contextListBackground,
  "&.vertical": {
    // remove left margin in vertical mode so that it aligns with tabs.
    margin: "8px 8px 8px 0px",
    minHeight: 400,
  },
});

const eventFormContentStyles = (theme: Theme): object => ({
  display: "flex",
  padding: 0,
  flexDirection: "column",
  overflow: "hidden",
  transform: "scale(0,0)",
  transition: theme.transitions.create("transform", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.shorter,
  }),
  "&.show": {
    transform: "scale(1,1)",
    transition: theme.transitions.create("transform", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shorter,
    }),
  },
  "&.hide": {
    display: "none",
  },
  "& .form-title": {
    color: theme.palette.primary.contrastText,
    padding: "2px",
  },
});

const eventIdStyles = (theme: Theme): object => ({
  "& .copyToClipboard": {
    margin: "0 !important",
    padding: "5px 15px !important",
    color: theme.custom.forms.labels,
    opacity: 0.25,
    "&:hover": {
      opacity: 0.75,
    },
    "& .label": { fontSize: 12 },
    "& .icon": { height: 15, width: 15, cursor: "pointer", marginLeft: "5px" },
  },
});

/** Function created to filter out flight fields sent to child forms so that their
 *  payloads never accidentally contain info they shouldn't be allowed to modify. */
export const filterApplicableFields = (f: IFullFlight, tabId: string): IEditPartialFlight => {
  //! We can't use tabIndex here since it's possible that they'd be modified by incoming custom schemas.
  switch (true) {
    case tabId === "Flight":
      return {
        _id: f?._id,
        flightNumber: f?.flightNumber,
        flightNumberSuffix: f?.flightNumberSuffix,
        isReleased: f?.isReleased,
        flightType: f?.flightType,
        origin: f?.origin,
        departureGate: f?.departureGate,
        destination: f?.destination,
        arrivalGate: f?.arrivalGate,
        // Diversion fields required only only if divertedFrom populated
        divertedFrom: f?.divertedFrom,
        diversionReason: f?.diversionReason,
        scheduledOut: f?.scheduledOut,
        scheduledOff: f?.scheduledOff,
        receivedDepartureMessage: f?.receivedDepartureMessage,
        scheduledOn: f?.scheduledOn,
        scheduledIn: f?.scheduledIn,
        receivedArrivalMessage: f?.receivedArrivalMessage,
        actualOut: f?.actualOut,
        actualOff: f?.actualOff,
        actualOn: f?.actualOn,
        actualIn: f?.actualIn,
        // Optional fields
        aircraft: f?.aircraft,
        aircraftConfiguration: f?.aircraftConfiguration,
        customer: f?.customer,
        note: f?.note,
        flags: f?.flags,
        reInitializeForm: f?.reInitializeForm,
      };
    case tabId === "Delays":
      return {
        _id: f?._id,
        delays: f?.delays,
        origin: f?.origin,
        destination: f?.destination,
        scheduledOut: f?.scheduledOut,
        scheduledIn: f?.scheduledIn,
        actualOut: f?.actualOut,
        actualIn: f?.actualIn,
        reInitializeForm: f?.reInitializeForm,
      };
    case tabId === "Crews":
      return { _id: f?._id, riders: f?.riders, reInitializeForm: f?.reInitializeForm };
    case tabId === "Comments":
      return { _id: f?._id, comments: f?.comments, reInitializeForm: f?.reInitializeForm };
    case tabId === "Messages":
      return {
        _id: f?._id,
        departureMessages: f?.departureMessages,
        arrivalMessages: f?.arrivalMessages,
        reInitializeForm: f?.reInitializeForm,
      };
    case tabId === "Log":
      return {
        _id: f?._id,
        takeOff: f?.takeOff,
        landing: f?.landing,
        sector: f?.sector,
        reInitializeForm: f?.reInitializeForm,
      };
    case tabId === "Custom":
      return { _id: f?._id, custom: f?.custom, reInitializeForm: f?.reInitializeForm };
    default:
      return f;
  }
};

/**
 * Function Component wrapper for the ActiveFlight component.
 * Will eventually replace the ActiveFlight class component.
 * Passes orientation of the mosaic pane (vertical or horizontal) to child.
 * @param props -
 */
export const BetaEventEditor = (props: IEventEditorProps): JSX.Element => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<"horizontal" | "vertical">("horizontal");
  const { id, path, windowProps, setCloseWarning } = props;
  const { isFlight, eventId, aircraftId, removalType } = windowProps;
  const dispatch = useDispatch();

  useLayoutEffect(() => {
    const { width, height } = containerRef.current.getBoundingClientRect();
    const newOrientation = width > height ? "horizontal" : "vertical";
    setLayout(newOrientation);
  }, [path]);

  // Display Warning Modal
  const [dialogModal, setDialogModal] = useState<IAlert>(null);
  const renderDialogModal = useCallback((title: string, message: string): void => {
    const dialogData = new AlertModel({
      title,
      message,
      buttons: [
        {
          label: "Close Warning",
          handler: () => setDialogModal(null),
        },
      ],
    });
    setDialogModal(dialogData as IAlert);
  }, []);

  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

  // Track dirty state for all nested tab forms. (shows edit icon near tab if dirty)
  const [tabDirtyStates, setTabDirtyStates] = useState<any>({});
  const handleSetTabDirty = useCallback((tabId: string, state: boolean) => {
    setTabDirtyStates((prev: any) => ({ ...prev, [tabId]: state }));
  }, []);

  const isDirty = useMemo(() => {
    return Object.values(tabDirtyStates).indexOf(true) > -1;
  }, [tabDirtyStates]);

  // Form handle registry: Formik-based tabs register their submitForm so we can trigger save-all.
  const formHandlesRef = useRef<Map<string, IFormHandle>>(new Map());
  const registerFormHandle = useCallback((tabId: string, handle: IFormHandle | null) => {
    handle == null
      ? formHandlesRef.current.delete(tabId)
      : formHandlesRef.current.set(tabId, handle);
  }, []);

  /**
   * Save all dirty Formik-based tabs by calling each registered form's submitForm.
   * Called by any tab when its own Update button is clicked, ensuring all dirty tabs submit.
   * Uses a guard flag to prevent cascading: when tab A's save triggers tab B's submitForm,
   * tab B's handleSubmit should NOT trigger another round of save-all.
   * @param excludeTabId - The tab that initiated the save (it will submit itself via Formik's native flow).
   */
  const isSavingAllRef = useRef(false);
  const saveAllDirtyTabs = useCallback(async (excludeTabId?: string) => {
    if (isSavingAllRef.current) {
      return; // Prevent cascading save-all
    }
      
    isSavingAllRef.current = true;
    try {
      await Promise.all(
        [...formHandlesRef.current].map(([tabId, handle]) => {
          if (tabId !== excludeTabId && handle.isDirty()) {
            return handle.submitForm();
          }
          return Promise.resolve();
        })
      );
    } finally {
      isSavingAllRef.current = false;
    }
  }, []);

  // Have child tabs set this to true when making async calls (to lock out ability to switch tabs mid call);
  const [APICallStatus, setAPICallStatus] = useState<string | null>(null);

  // Store current value for flight being edited here. (all individual forms used inside of tabs should update this value when submitted!!) - CC
  const [loadedFlight, setLoadedFlight] = useState<IFullFlight>(null);
  const [loadedCrew, setLoadedCrew] = useState<Record<string, IFlightPairingDetails[]>>(null);
  const [loadedGroundEvent, setLoadedGroundEvent] = useState<IGroundEvent>(null);
  const [loadedCanceledFlight, setLoadedCanceledFlight] = useState<ICanceledFlight>(null);

  // Track any changes that occur to flights while an editor is open. Warn the user if flights are modified outside an editor context.
  const flights = useSelector((state: IRootState) => state.timelineFlights.flightData);

  const setLoadedEvent = useCallback(
    (value: IGroundEvent | IFullFlight) => {
      if (isFlight) {
        setLoadedFlight(value as IFullFlight);
      } else setLoadedGroundEvent(value as IGroundEvent);
    },
    [isFlight]
  );

  const thisTimeFlight = useMemo(() => {
    if (loadedFlight?._id) {
      const flight = flights?.[aircraftId ?? unassignedAircraftId]?.flights?.find((f) => f._id === loadedFlight._id);
      if (flight?._id) {
        // If flight was found return it.
        return flight;
      } else {
        // Else it may have been reassigned so check all other aircraft for it.
        for (const value of Object.values(flights)) {
          const reassignedFlight = value.flights.find((f) => f._id === loadedFlight._id);
          if (reassignedFlight?._id) {
            return reassignedFlight;
          }
        }
        // If not found anywhere (deleted, cancelled), return null;
        return null;
      }
    }
    return null;
  }, [loadedFlight?._id, aircraftId, flights]);

  const thisTimeEvent = useMemo(() => {
    if (loadedGroundEvent?._id) {
      const event = flights?.[aircraftId ?? unassignedAircraftId]?.groundEvents?.find(
        (f) => f._id === loadedGroundEvent._id
      );
      if (event?._id) {
        // If event was found return it.
        return event;
      } else {
        // Else it may have been reassigned so check all other aircraft for it.
        for (const value of Object.values(flights)) {
          const reassignedEvent = value.groundEvents.find((f) => f._id === loadedGroundEvent._id);
          if (reassignedEvent?._id) {
            return reassignedEvent;
          }
        }
        // If not found anywhere (deleted, cancelled), return null;
        return null;
      }
    }
    return null;
  }, [loadedGroundEvent?._id, aircraftId, flights]);

  // Get last modified info for flight/ground event so the forms can display it.
  const lastModifiedInfo: ILastModifiedInfo = useMemo(() => {
    return thisTimeFlight?.modifiedAt
      ? {
          modifiedAt: new Date(thisTimeFlight?.modifiedAt),
          modifiedBy: thisTimeFlight?.modifiedBy,
        }
      : thisTimeEvent?.modifiedAt
        ? {
            modifiedAt: new Date(thisTimeEvent?.modifiedAt),
            modifiedBy: thisTimeEvent?.modifiedBy,
          }
        : {
            modifiedAt: new Date(), // set today's date.
            modifiedBy: null, // unknown
          };
  }, [thisTimeFlight, thisTimeEvent]);

  const tabCounts = useMemo(() => {
    return {
      delayCount: loadedFlight?.delays?.length ? `(${loadedFlight?.delays?.length})` : "",
      commentCount: loadedFlight?.comments?.length ? `(${loadedFlight?.comments?.length})` : "",
      messageCount:
        loadedFlight?.departureMessages?.length || loadedFlight?.arrivalMessages?.length
          ? `(${loadedFlight?.departureMessages?.length + loadedFlight?.arrivalMessages?.length})`
          : "",
      contentCount: loadedFlight?.content?.length ? `(${loadedFlight?.content?.length})` : "",
      geCommentCount: loadedGroundEvent?.comments?.length ? `(${loadedGroundEvent?.comments?.length})` : "",
    };
  }, [loadedFlight, loadedGroundEvent]);

  const getFullFlightAction = useCallback(async (id: string): Promise<void> => {
    setAPICallStatus("Loading Flight");
    const response = await AmplifyAPIWrapper.Get<IFullFlight>(`/flights/${id}`);
    if (response?._id) {
      // As a precaution, add the most up to date version of this event to the timeline data. -- CC
      addEventToTimeline(response, "flight", store.dispatch, () => store.getState());
      setLoadedFlight({ ...response, reInitializeForm: true });
    }
    setAPICallStatus(null);
  }, []);

  const getGroundEventAction = useCallback(async (id: string): Promise<void> => {
    setAPICallStatus("Loading Ground Event");
    const response = await AmplifyAPIWrapper.Get<IGroundEvent>(`/groundEvents/${id}`);
    if (response?._id) {
      const fixedTimes = returnEpochValuesAsNumbers(response);
      // As a precaution, add the most up to date version of this event to the timeline data. -- CC
      addEventToTimeline(fixedTimes, "event", store.dispatch, () => store.getState());
      setLoadedGroundEvent({ ...fixedTimes, reInitializeForm: true });
    }
    setAPICallStatus(null);
  }, []);

  const getCanceledFlightAction = useCallback(async (id: string): Promise<void> => {
    setAPICallStatus("Loading Canceled Flight");
    const response = await AmplifyAPIWrapper.Get<ICanceledFlight>(`/flights/canceled/${id}`);
    if (response?._id) {
      setLoadedCanceledFlight(response);
    }
    setAPICallStatus(null);
  }, []);

  useEffect(() => {
    if (isFlight && eventId && getFullFlightAction && !removalType && !loadedFlight) {
      const getCrewAction = async (): Promise<void> => {
        setAPICallStatus("Loading Crew");
        const response = await AmplifyAPIWrapper.Get<Record<string, IFlightPairingDetails[]>>(
          `/flight/${eventId}/crew`
        );
        if (response) {
          setLoadedCrew(response);
        }
        setAPICallStatus(null);
      };
      // LOAD FLIGHT
      getFullFlightAction(eventId);
      // LOAD CREW
      getCrewAction();
    } else if (!isFlight && eventId && getGroundEventAction && !loadedGroundEvent) {
      const getCrewAction = async (): Promise<void> => {
        setAPICallStatus("Loading Crew");
        //todo make this for ground events
        const response = await AmplifyAPIWrapper.Get<Record<string, IFlightPairingDetails[]>>(
          `/groundEvents/${eventId}/crew`
        );
        if (response) {
          setLoadedCrew(response);
        }
        setAPICallStatus(null);
      };
      // LOAD GROUND EVENT
      getGroundEventAction(eventId);
      // LOAD CREW
      getCrewAction();
    } else if (isFlight && eventId && removalType === FlightRemovalType.CancelFlight && getCanceledFlightAction) {
      // LOAD CANCELED FLIGHT
      getCanceledFlightAction(eventId);
    }
  }, [
    loadedFlight,
    loadedGroundEvent,
    eventId,
    isFlight,
    getFullFlightAction,
    getGroundEventAction,
    getCanceledFlightAction,
    removalType,
  ]);

  // APIS Stuff
  const [APISModalOpen, setAPISModalOpen] = useState<boolean>(false);
  const policies = useSelector((state: IRootState) => state.authentication?.attributes?.policies);
  const isAPISCustomer = useSelector(
    (state: IRootState) => state.structures.customerStructure.globalAppConfig.customer?.settings?.isAPISEnabled
  );
  const hasAPISPermissions = useMemo(
    () => hasPermission(policies, PolicyResource.APIS, PolicyAction.Create),
    [policies]
  );

  const customFlightTabs: ICustomTab[] = useSelector((state: IRootState) => state.customPages.flight);
  const customGroundEventPages: {
    [eventType: string]: ICustomTab[];
  } = useSelector((state: IRootState) => state.customPages.groundEvent);

  //The custom fields on a GE are defined by their event type
  const customGroundEventTabs: ICustomTab[] = useMemo(
    () =>
      loadedGroundEvent?.eventType?.title && customGroundEventPages[loadedGroundEvent?.eventType?.title]
        ? customGroundEventPages[loadedGroundEvent?.eventType?.title]
        : [],
    [loadedGroundEvent, customGroundEventPages]
  );

  const refreshClick = useCallback(
    (eventId: string) => {
      // eslint-disable-next-line @typescript-eslint/require-await
      const ffModalRefresh = async (): Promise<void> => {
        dispatch(
          openModal({
            root: {
              data: {
                title: `Refresh ${isFlight ? "Flight" : "Ground Event"} Warning`,
                message: "Refreshing will clear all unsaved changes. Continue?",
                buttons: [
                  {
                    label: "CANCEL",
                    handler: () => {
                      dispatch(closeModal({ root: { data: null } }));
                    },
                  },
                  {
                    label: "OK",
                    handler: () => {
                      void (async () => {
                        if (isFlight) await getFullFlightAction(eventId);
                        else await getGroundEventAction(eventId);
                        dispatch(closeModal({ root: { data: null } }));
                      })();
                    },
                  },
                ],
              },
            },
          })
        );
      };
      const ffRefresh = async (): Promise<void> => {
        if (isFlight) await getFullFlightAction(eventId);
        else await getGroundEventAction(eventId);
      };
      if (isDirty) void ffModalRefresh();
      else void ffRefresh();
    },
    [dispatch, getFullFlightAction, getGroundEventAction, isDirty, isFlight]
  );

  const defaultFlightTabs: ITabElement[] = useMemo(() => {
    return [
      { wrapper: FormTabWrapper("Flight", tabDirtyStates["Flight"]), id: "Flight" },
      { wrapper: FormTabWrapper(isAPISCustomer ? "Crew / Riders" : "Crew", tabDirtyStates["Crew"]), id: "Crew" },
      { wrapper: FormTabWrapper(`Content ${tabCounts.contentCount}`, tabDirtyStates["Content"]), id: "Content" },
      { wrapper: FormTabWrapper(`Messages ${tabCounts.messageCount}`, tabDirtyStates["Messages"]), id: "Messages" },
      { wrapper: FormTabWrapper(`Delay Details ${tabCounts.delayCount}`, tabDirtyStates["Delays"]), id: "Delays" },
      { wrapper: FormTabWrapper("Log", tabDirtyStates["Log"]), id: "Log" },
      { wrapper: FormTabWrapper(`Comments ${tabCounts.commentCount}`, tabDirtyStates["Comments"]), id: "Comments" },
    ];
  }, [tabDirtyStates, isAPISCustomer, tabCounts]);

  const defaultGroundEventTabs: ITabElement[] = useMemo(() => {
    return loadedGroundEvent?.eventType?.isCrewable
      ? [
          { wrapper: FormTabWrapper("GroundEvent", tabDirtyStates["GroundEvent"]), id: "GroundEvent" },
          { wrapper: FormTabWrapper("Crew", tabDirtyStates["Crew"]), id: "Crew" },
          {
            wrapper: FormTabWrapper(`Comments ${tabCounts.geCommentCount}`, tabDirtyStates["GEComments"]),
            id: "GEComments",
          },
        ]
      : [
          { wrapper: FormTabWrapper("GroundEvent", tabDirtyStates["GroundEvent"]), id: "GroundEvent" },
          {
            wrapper: FormTabWrapper(`Comments ${tabCounts.geCommentCount}`, tabDirtyStates["GEComments"]),
            id: "GEComments",
          },
        ];
  }, [loadedGroundEvent?.eventType?.isCrewable, tabDirtyStates, tabCounts.geCommentCount]);

  const defaultCanceledFlightTabs: ITabElement[] = useMemo(() => {
    return [{ wrapper: FormTabWrapper("CanceledFlight", tabDirtyStates["CanceledFlight"]), id: "CanceledFlight" }];
  }, [tabDirtyStates]);

  const tabWrappers = useMemo(
    () =>
      isFlight
        ? removalType
          ? getEventEditorTabs([], defaultCanceledFlightTabs, tabDirtyStates)
          : getEventEditorTabs(customFlightTabs, defaultFlightTabs, tabDirtyStates)
        : getEventEditorTabs(customGroundEventTabs, defaultGroundEventTabs, tabDirtyStates),
    [
      isFlight,
      removalType,
      defaultCanceledFlightTabs,
      tabDirtyStates,
      customFlightTabs,
      defaultFlightTabs,
      customGroundEventTabs,
      defaultGroundEventTabs,
    ]
  );
  const selectedTab = tabWrappers[activeTabIndex];

  return (
    <div ref={containerRef} style={{ display: "flex", width: "100%", height: "100%" }}>
      {APICallStatus === null ? (
        <Grid
          container
          alignContent="flex-start"
          sx={eventEditorStyles(theme)}
          style={{ flexDirection: layout === "vertical" ? "row" : "column" }}
        >
          <Grid
            item
            sx={activeEventMenuStyles(theme)}
            classes={{
              root: classNames(layout),
            }}
          >
            <div className="flightchip_container" style={{ overflow: layout === "vertical" ? "hidden" : "visible" }}>
              {loadedFlight && <FlightChip data={loadedFlight} />}
              {loadedCanceledFlight && <FlightChip data={loadedCanceledFlight} />}
              {loadedGroundEvent && <EventChip data={loadedGroundEvent} />}
            </div>
            <Button
              sx={layout == "horizontal" ? { marginLeft: ".5vw" } : {}}
              onClick={(event: any) => refreshClick(eventId)}
            >
              Refresh
            </Button>
            <OcTabs
              variant="scrollable"
              orientation={layout}
              scrollButtons="auto"
              value={activeTabIndex}
              onChange={(event: any, value: any) => setActiveTabIndex(value)}
            >
              {tabWrappers.map((tabWrapper) => tabWrapper.wrapper)}
            </OcTabs>
          </Grid>

          <Grid
            item
            style={{
              minWidth: 400, // never allow editor to be shrunk down to less than 400.
              width: layout === "vertical" ? "calc(100% - 135px)" : "100%",
              height: layout === "vertical" ? "fit-content" : "auto",
            }}
          >
            {loadedFlight?._id && (
              <Card sx={activeEventCardStyles(theme)} classes={{ root: classNames(layout, "flight_card") }}>
                <EditorChangeWarning
                  eventId={loadedFlight?._id}
                  time={loadedFlight.modifiedAt ?? loadedFlight.createdAt} // Use initial values here
                  outsideModification={lastModifiedInfo}
                  refreshForm={getFullFlightAction}
                  eventType="Flight"
                />
                <WarningModal displayModal={isDirty} setCloseWarning={setCloseWarning} />

                <Grid item style={{ display: selectedTab?.id === "Flight" ? "flex" : "none" }}>
                  <FlightTab
                    fullFlight={loadedFlight}
                    setLoadedFlight={setLoadedFlight} // Update loaded flight values
                    updateTabState={(state) => handleSetTabDirty("Flight", state)}
                    mosaicId={id}
                    setAPICallStatus={setAPICallStatus}
                    renderDialogModal={renderDialogModal}
                    setAPISModalOpen={setAPISModalOpen}
                    hasAPISPermissions={hasAPISPermissions}
                    isAPISCustomer={isAPISCustomer}
                    lastModifiedInfo={lastModifiedInfo}
                    registerFormHandle={registerFormHandle}
                    saveAllDirtyTabs={saveAllDirtyTabs}
                  />
                </Grid>

                <Grid item style={{ display: selectedTab?.id === "Delays" ? "flex" : "none" }}>
                  <DelaysTab
                    fullFlight={loadedFlight}
                    updateTabState={(state) => handleSetTabDirty("Delays", state)}
                    mosaicId={id}
                    setAPICallStatus={setAPICallStatus}
                    setLoadedFlight={setLoadedFlight} // Update loaded flight values
                  />
                </Grid>

                <Grid item style={{ display: selectedTab?.id === "Crew" ? "flex" : "none" }}>
                  <CrewTab
                    fullFlight={loadedFlight}
                    updateTabState={(state) => handleSetTabDirty("Crew", state)}
                    mosaicId={id}
                    setAPICallStatus={setAPICallStatus}
                    isAPISCustomer={isAPISCustomer}
                    crewOnFlight={loadedCrew}
                    setLoadedFlight={setLoadedFlight} // Update loaded flight values
                  />
                </Grid>

                <Grid item style={{ display: selectedTab?.id === "Comments" ? "flex" : "none" }}>
                  <CommentsTab
                    fullFlight={loadedFlight}
                    updateTabState={(state) => handleSetTabDirty("Comments", state)}
                    mosaicId={id}
                    setAPICallStatus={setAPICallStatus}
                    setLoadedFlight={setLoadedFlight} // Update loaded flight values
                  />
                </Grid>

                <Grid item style={{ display: selectedTab?.id === "Messages" ? "flex" : "none" }}>
                  <MessagesTab
                    fullFlight={loadedFlight}
                    updateTabState={(state) => handleSetTabDirty("Messages", state)}
                    mosaicId={id}
                    setAPICallStatus={setAPICallStatus}
                    // Doesn't need to update loaded flight as it's displaying messages only.
                  />
                </Grid>

                <Grid item style={{ display: selectedTab?.id === "Content" ? "flex" : "none" }}>
                  <ContentTab
                    fullFlight={loadedFlight}
                    updateTabState={(state) => handleSetTabDirty("Content", state)}
                    mosaicId={id}
                    path={path}
                    setAPICallStatus={setAPICallStatus}
                    setLoadedEvent={setLoadedEvent}
                    // Doesn't need to update loaded flight as it's displaying content only.
                  />
                </Grid>

                <Grid item style={{ display: selectedTab?.id === "Log" ? "flex" : "none" }}>
                  <LogTab
                    fullFlight={loadedFlight}
                    updateTabState={(state) => handleSetTabDirty("Log", state)}
                    mosaicId={id}
                    setAPICallStatus={setAPICallStatus}
                    crewOnFlight={loadedCrew}
                    setLoadedFlight={setLoadedFlight} // Update loaded flight values
                    registerFormHandle={registerFormHandle}
                    saveAllDirtyTabs={saveAllDirtyTabs}
                  />
                </Grid>

                {customFlightTabs?.map((customContent) => {
                  return (
                    <Grid
                      key={`custom_tab_${customContent?.title}`}
                      item
                      style={{ display: selectedTab?.id === customContent?.title ? "flex" : "none" }}
                    >
                      <CustomTab
                        fullEvent={loadedFlight}
                        customEventTab={customContent}
                        updateTabState={(state) => handleSetTabDirty(customContent?.title, state)}
                        mosaicId={id}
                        setAPICallStatus={setAPICallStatus}
                        setLoadedEvent={setLoadedEvent}
                        registerFormHandle={registerFormHandle}
                        saveAllDirtyTabs={saveAllDirtyTabs}
                      />
                    </Grid>
                  );
                })}
                <Box sx={eventIdStyles(theme)}>
                  <CopyToClipboard
                    label={`Flight ID ${loadedFlight?._id}`}
                    copyValue={loadedFlight?._id}
                    className={"copyToClipboard"}
                  />
                  <CopyToClipboard
                    label={`Batch ID ${loadedFlight?.batchId}`}
                    copyValue={loadedFlight?.batchId}
                    className={"copyToClipboard"}
                  />
                </Box>
              </Card>
            )}

            {loadedCanceledFlight?._id && (
              <Card sx={activeEventCardStyles(theme)} classes={{ root: classNames(layout, "flight_card") }}>
                <Grid item style={{ display: selectedTab?.id === "CanceledFlight" ? "flex" : "none" }}>
                  <CancelFlightTab
                    canceledFlight={loadedCanceledFlight}
                    setLoadedCanceledFlight={setLoadedCanceledFlight} // Update loaded flight values
                    updateTabState={(state) => handleSetTabDirty("CanceledFlight", state)}
                    mosaicId={id}
                    setAPICallStatus={setAPICallStatus}
                  />
                </Grid>
                <Box sx={eventIdStyles(theme)}>
                  <CopyToClipboard
                    label={`Flight ID ${loadedCanceledFlight?._id}`}
                    copyValue={loadedCanceledFlight?._id}
                    className={"copyToClipboard"}
                  />
                  <CopyToClipboard
                    label={`Batch ID ${loadedCanceledFlight?.batchId}`}
                    copyValue={loadedCanceledFlight?.batchId}
                    className={"copyToClipboard"}
                  />
                </Box>
              </Card>
            )}

            {loadedGroundEvent?._id && (
              <Card sx={activeEventCardStyles(theme)} classes={{ root: classNames(layout, "groundevent_card") }}>
                <EditorChangeWarning
                  eventId={loadedGroundEvent?._id}
                  time={loadedGroundEvent.modifiedAt ?? loadedGroundEvent.createdAt} // Use initial values here
                  outsideModification={lastModifiedInfo}
                  refreshForm={getGroundEventAction}
                  eventType="Ground Event"
                />
                <Grid item style={{ display: selectedTab?.id === "GroundEvent" ? "flex" : "none" }}>
                  <GroundEventTab
                    groundEvent={loadedGroundEvent}
                    setLoadedGroundEvent={setLoadedGroundEvent} // Updated loaded ground event values on succesfull update
                    updateTabState={(state) => handleSetTabDirty("GroundEvent", state)}
                    mosaicId={id}
                    setAPICallStatus={setAPICallStatus}
                    renderDialogModal={renderDialogModal}
                    lastModifiedInfo={lastModifiedInfo}
                    registerFormHandle={registerFormHandle}
                    saveAllDirtyTabs={saveAllDirtyTabs}
                  />
                </Grid>
                <Grid item style={{ display: selectedTab?.id === "GEComments" ? "flex" : "none" }}>
                  <GECommentsTab
                    groundEvent={loadedGroundEvent}
                    setLoadedGroundEvent={setLoadedGroundEvent}
                    updateTabState={(state) => handleSetTabDirty("GEComments", state)}
                    mosaicId={id}
                    setAPICallStatus={setAPICallStatus}
                  />
                </Grid>
                {loadedCrew && loadedGroundEvent?.eventType?.isCrewable && (
                  <Grid item style={{ display: selectedTab?.id === "Crew" ? "flex" : "none" }}>
                    <CrewTab
                      // fullFlight={loadedFlight}
                      // updateTabState={(state) => handleSetTabDirty("Crew", state)}
                      mosaicId={id}
                      // setAPICallStatus={setAPICallStatus}
                      // isAPISCustomer={false}
                      crewOnFlight={loadedCrew}
                      // setLoadedFlight={setLoadedFlight} // Update loaded flight values
                    />
                  </Grid>
                )}

                {customGroundEventTabs?.map((customContent) => {
                  return (
                    <Grid
                      key={`custom_tab_${customContent?.title}`}
                      item
                      style={{ display: selectedTab?.id === customContent?.title ? "flex" : "none" }}
                    >
                      <CustomTab
                        fullEvent={loadedGroundEvent}
                        customEventTab={customContent}
                        updateTabState={(state) => handleSetTabDirty(customContent?.title, state)}
                        mosaicId={id}
                        setAPICallStatus={setAPICallStatus}
                        setLoadedEvent={setLoadedEvent}
                        registerFormHandle={registerFormHandle}
                        saveAllDirtyTabs={saveAllDirtyTabs}
                      />
                    </Grid>
                  );
                })}
                <Box sx={eventIdStyles(theme)}>
                  <CopyToClipboard
                    label={`Ground Event ID: ${loadedGroundEvent?._id}`}
                    copyValue={loadedGroundEvent?._id}
                    className={"copyToClipboard"}
                  />
                  <CopyToClipboard
                    label={`Ground Event Batch ID: ${loadedGroundEvent?.batchId}`}
                    copyValue={loadedGroundEvent?.batchId}
                    className={"copyToClipboard"}
                  />
                </Box>
              </Card>
            )}
          </Grid>
        </Grid>
      ) : (
        <SpinnerProgress loading={!!APICallStatus} message={APICallStatus} />
      )}
      {dialogModal && <DialogModal fullscreen={false} data={dialogModal} />}
      {APISModalOpen && (
        <SendAPISMessageModal
          isOpen={APISModalOpen}
          handleClose={() => setAPISModalOpen(false)}
          flight={loadedFlight}
        />
      )}
    </div>
  );
};
